import type { Correspondence, Palette, Planet, SigilNode } from "../types";
import { generateWheelNodes, localCorrespondence, reduceIntent, type ReductionRules } from "../utils/sigil";

interface PipelineEnvelope<T> { ok: true; source: "luna" | "local"; offline: boolean; result: T }

async function request<T>(action: string, input: Record<string, unknown>, fallback: () => T): Promise<PipelineEnvelope<T>> {
  try {
    const response = await fetch("/api/occult/pipeline", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, input }),
    });
    if (!response.ok) throw new Error("Companion offline");
    return await response.json() as PipelineEnvelope<T>;
  } catch {
    return { ok: true, source: "local", offline: true, result: fallback() };
  }
}

export interface ShrineReflection {
  reflection: string;
  palette: Palette;
  offerings: string[];
  note: string;
}

export interface NovenaPrayer {
  title: string; opening: string; petition: string; refrain: string; days: string[]; closing: string;
}

export function reflectOnPetition(petition: string) {
  return request<ShrineReflection>("shrine_reflection", { petition }, () => {
    const correspondence = localCorrespondence(petition);
    return {
      reflection: `Hold this petition without haste. Name what is yours to do, what must be released, and what help you are asking to receive. Return to the request with honesty rather than certainty.`,
      palette: correspondence.palette,
      offerings: correspondence.element === "fire" ? ["candle", "copal"] : ["fresh water", "white flowers"],
      note: "Local reflection; no outcome is promised.",
    };
  });
}

export function generateNovena(petition: string) {
  return request<NovenaPrayer>("shrine_prayer", { petition }, () => ({
    title: "Nine-day petition structure",
    opening: "Santísima Muerte, I approach with respect, clear speech, and gratitude for the life before me.",
    petition: `I place before you this honest request: ${petition}`,
    refrain: "Grant me steadiness to act well, wisdom to recognize what is given, and courage to accept what must change.",
    days: ["Clarify the request.", "See obstacles clearly.", "Give thanks for protection.", "Repair a neglected duty.", "Listen without bargaining.", "Make a sincere offering.", "Pray for all affected.", "Release the answer's exact shape.", "Close with thanks and fulfill the vow."],
    closing: "May this request be answered only in ways that preserve dignity, consent, justice, and the good of all concerned.",
  }));
}

export function refineIntent(intent: string, rules: ReductionRules) {
  return request<{ refined: string; reduced: string }>("intent_refine", { intent, rules }, () => {
    const refined = `MY WILL IS ${intent.replace(/\b(i want|i wish|please|maybe|try to)\b/gi, "").trim().toUpperCase()}`.slice(0, 220);
    return { refined, reduced: reduceIntent(refined, rules) };
  });
}

export function lookupCorrespondences(intent: string) {
  return request<Correspondence>("correspondences", { intent }, () => localCorrespondence(intent));
}

export function generatePipelineNodes(intent: string, reduced: string, planet: Planet, rules: ReductionRules) {
  return request<{ reduced: string; planet: Planet; element: Correspondence["element"]; nodes: SigilNode[] }>(
    "sigil_nodes", { intent, reduced, planet, rules },
    () => ({ reduced, planet, element: localCorrespondence(intent).element, nodes: generateWheelNodes(reduced, planet) }),
  );
}
